// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — Contractors
// Two-layer model:
//   Layer 1 — Organizations directory: identity entered once, reused across incidents
//   Layer 2 — Incident engagement: contract #, amount, status, notes per incident
// When an org is linked, identity fields are read-only from the directory.
// When unlinked, fields are entered manually with an option to register the org.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconBuilding, IconX, IconCheck, IconLink,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useDebrisStore } from '../../store/debris-store';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { queryRecords, fetchRecord, createRecord } from '../../api/client';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';
import type { DebrisContractor, ContractorStatus } from '../../types/debris';
import { formatPhone } from '../../lib/phoneFormat';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--color-success)', suspended: 'var(--color-error)', completed: 'var(--color-primary-700)',
};

type OrgCard = {
  record_id: number;
  name: string;
  type: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  licenseNumber: string;
  specialties: string;
  fein: string;
};

const EMPTY_INCIDENT: Partial<DebrisContractor> = {
  contractNumber: '', contractAmount: '', status: 'active', notes: '',
};

const EMPTY_MANUAL: Partial<DebrisContractor> = {
  companyName: '', contractNumber: '', contractAmount: '',
  contactName: '', contactPhone: '', contactEmail: '',
  status: 'active', notes: '',
};

const FO = FIELDS.organizations;

function parseOrgCard(r: Record<string, unknown>): OrgCard {
  return {
    record_id: r.record_id as number,
    name: String(r[FO.name] ?? ''),
    type: String(r[FO.type] ?? ''),
    phone: String(r[FO.phone] ?? ''),
    email: String(r[FO.email] ?? ''),
    city: String(r[FO.city] ?? ''),
    state: String(r[FO.state] ?? ''),
    licenseNumber: String(r[FO.licenseNumber] ?? ''),
    specialties: String(r[FO.specialties] ?? ''),
    fein: String(r[FO.fein] ?? ''),
  };
}

// ── Linked Org Card ─────────────────────────────────────────────
function OrgLinkedCard({ org, onClear }: { org: OrgCard; onClear: () => void }) {
  return (
    <div style={{
      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 4,
      padding: '16px 16px', display: 'flex', gap: 8, alignItems: 'flex-start',
    }}>
      <IconBuilding size={18} style={{ color: '#16a34a', marginTop: 2, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#166534' }}>{org.name}</span>
          {org.type && <Badge size="xs" color="var(--color-success)" variant="light">{org.type}</Badge>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12, color: '#374151' }}>
          {(org.city || org.state) && <span>{[org.city, org.state].filter(Boolean).join(', ')}</span>}
          {org.phone && <span>{formatPhone(org.phone)}</span>}
          {org.email && <span>{org.email}</span>}
          {org.licenseNumber && <span>License: {org.licenseNumber}</span>}
          {org.fein && <span>FEIN: {org.fein}</span>}
        </div>
        {org.specialties && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{org.specialties}</div>
        )}
      </div>
      <button
        onClick={onClear}
        title="Unlink organization"
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, flexShrink: 0 }}
      >
        <IconX size={14} />
      </button>
    </div>
  );
}

// ── Org Search Typeahead ─────────────────────────────────────────
function OrgSearch({ onSelect }: { onSelect: (org: OrgCard) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<OrgCard[]>([]);
  const [searching, setSearching] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQuery = (q: string) => {
    setQuery(q);
    if (debounce.current) clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); return; }
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await queryRecords<Record<string, unknown>>(TABLES.organizations, {
          filters: [
            { field: FO.type, operator: 'in', value: 'contractor,partner,government,ngo,business,utility,other' },
            { field: FO.status, operator: 'eq', value: 'active' },
          ],
          sort: { field: FO.name, direction: 'asc' },
          limit: 20,
        });
        const lower = q.toLowerCase();
        setResults(
          res.records
            .map(parseOrgCard)
            .filter(o => o.name.toLowerCase().includes(lower) || o.specialties.toLowerCase().includes(lower))
        );
      } catch { /* ignore */ }
      setSearching(false);
    }, 280);
  };

  return (
    <div>
      <Text size="xs" fw={600} c="dimmed" mb={4}>Link to Organizations Directory</Text>
      <div style={{ position: 'relative' }}>
        <TextInput
          placeholder="Search contractor by name…"
          size="xs"
          value={query}
          onChange={e => handleQuery(e.target.value)}
          leftSection={<IconSearch size={11} />}
          rightSection={searching ? <span style={{ fontSize: 12, color: '#aaa' }}>…</span> : undefined}
        />
        {(results.length > 0 || (query && !searching)) && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff',
            border: '1px solid #e2e4e8', borderRadius: 4, boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
            zIndex: 200, marginTop: 2, maxHeight: 220, overflowY: 'auto',
          }}>
            {results.length === 0 && query && (
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#888' }}>No matching contractors in directory</div>
            )}
            {results.map(o => (
              <div
                key={o.record_id}
                onClick={() => { onSelect(o); setQuery(''); setResults([]); }}
                style={{ padding: '8px 16px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 12 }}
              >
                <div style={{ fontWeight: 600 }}>{o.name}</div>
                <div style={{ color: '#6b7280' }}>
                  {[o.type, [o.city, o.state].filter(Boolean).join(', '), o.phone].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Contractors() {
  const { activeIncident } = useAuthStore();
  const { canEdit } = useModulePermission('debris');
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    contractors, loadingContractors,
    loadContractors, createContractor, updateContractor, deleteContractor,
  } = useDebrisStore();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DebrisContractor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DebrisContractor | null>(null);

  // Linked org (when selected from directory)
  const [linkedOrg, setLinkedOrg] = useState<OrgCard | null>(null);
  const [loadingOrg, setLoadingOrg] = useState(false);

  // Form for incident-specific fields (always) + identity fields (when unlinked)
  const [form, setForm] = useState<Partial<DebrisContractor>>({ ...EMPTY_MANUAL });
  const [saveToDir, setSaveToDir] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadContractors(); }, [loadContractors]);

  const iContractors = incidentId ? contractors : [];

  const filtered = useMemo(() => {
    let list = iContractors;
    if (statusFilter && statusFilter !== 'all') list = list.filter(c => c.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.companyName || '').toLowerCase().includes(q) ||
        (c.contractNumber || '').toLowerCase().includes(q) ||
        (c.contactName || '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
  }, [iContractors, statusFilter, search]);

  const sorted = [...filtered].sort((a, b) => {
    if (!sortField) return 0;
    const va = (a as any)[sortField] ?? '';
    const vb = (b as any)[sortField] ?? '';
    const n = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? n : -n;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageRows = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [statusFilter, search]);

  const setField = (key: keyof DebrisContractor, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY_MANUAL });
    setLinkedOrg(null);
    setSaveToDir(false);
    setModalOpen(true);
  };

  const openEdit = async (c: DebrisContractor) => {
    setEditTarget(c);
    setForm({ ...c });
    setSaveToDir(false);
    setLinkedOrg(null);

    // If this record is linked to an org, fetch and display its card
    if (c.orgId && TABLES.organizations) {
      setLoadingOrg(true);
      try {
        const raw = await fetchRecord<Record<string, unknown>>(TABLES.organizations, Number(c.orgId));
        if (raw) setLinkedOrg(parseOrgCard(raw));
      } catch { /* silently ignore */ }
      setLoadingOrg(false);
    }

    setModalOpen(true);
  };

  const clearOrg = () => {
    setLinkedOrg(null);
    // Preserve any manually-entered values so the user doesn't lose what they typed
  };

  const selectOrg = (org: OrgCard) => {
    setLinkedOrg(org);
    // Pre-populate identity fields from org so they're saved to the contractor record too
    // (useful for display even without re-fetching the org later)
    setForm(f => ({
      ...f,
      companyName: org.name,
      contactPhone: f.contactPhone || org.phone,
      contactEmail: f.contactEmail || org.email,
    }));
  };

  const handleSave = async () => {
    const isLinked = Boolean(linkedOrg);

    if (isLinked && !linkedOrg) return;
    if (!isLinked && !form.companyName?.trim()) {
      notifications.show({ message: 'Company name is required', color: '#C68700' });
      return;
    }

    setSaving(true);
    try {
      let orgId = editTarget?.orgId ?? (linkedOrg ? String(linkedOrg.record_id) : undefined);

      // Optionally register a new org for future use
      if (!isLinked && saveToDir && TABLES.organizations && !orgId) {
        const newOrg = await createRecord<Record<string, unknown>>(TABLES.organizations, {
          [FO.name]:            form.companyName,
          [FO.type]:            'contractor',
          [FO.contractorType]:  'debris',
          [FO.phone]:           form.contactPhone || null,
          [FO.email]:           form.contactEmail || null,
          [FO.status]:          'active',
        });
        orgId = String((newOrg.record_id ?? newOrg.id) as number);
      }

      const payload: Record<string, unknown> = {
        incidentId,
        companyName: isLinked ? linkedOrg!.name : form.companyName,
        contactName: isLinked ? '' : (form.contactName ?? ''),
        contactPhone: isLinked ? linkedOrg!.phone : (form.contactPhone ?? ''),
        contactEmail: isLinked ? linkedOrg!.email : (form.contactEmail ?? ''),
        contractNumber: form.contractNumber ?? '',
        contractAmount: form.contractAmount ?? '',
        status: form.status ?? 'active',
        notes: form.notes ?? '',
        ...(orgId ? { orgId } : {}),
      };

      if (editTarget) {
        await updateContractor(editTarget.id, payload);
        notifications.show({ message: 'Contractor updated', color: 'var(--color-success)' });
      } else {
        await createContractor(payload);
        notifications.show({ message: 'Contractor added', color: 'var(--color-success)' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContractor(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.companyName} removed`, color: '#939598' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Contractors</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContractors} onClick={() => loadContractors()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contractor</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput
          placeholder="Search company, contract #, contact..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="xs"
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          data={[{ value: 'all', label: 'All Statuses' }, ...STATUS_OPTIONS]}
          value={statusFilter ?? 'all'}
          onChange={v => setStatusFilter(v)}
          size="xs"
          w={160}
        />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contractor{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
                {[
                  { label: 'Company', field: 'companyName' },
                  { label: 'Contract #', field: 'contractNumber' },
                  { label: 'Contact', field: 'contactName' },
                  { label: 'Status', field: 'status' },
                  { label: 'Actions', field: '' },
                ].map(col => (
                  <th
                    key={col.label}
                    onClick={() => col.field && toggleSort(col.field)}
                    style={{
                      padding: '8px 16px',
                      textAlign: col.label === 'Actions' ? 'right' : 'left',
                      fontWeight: 600,
                      color: sortField === col.field && col.field ? '#4B3FFF' : undefined,
                      whiteSpace: 'nowrap',
                      cursor: col.field ? 'pointer' : 'default',
                      userSelect: 'none',
                    }}>
                    {col.label}
                    {col.field && (
                      <span style={{ marginLeft: 4, opacity: sortField === col.field ? 1 : 0.25 }}>
                        {sortField === col.field && sortDir === 'desc' ? '↓' : '↑'}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingContractors ? 'Loading...' : 'No contractors found'}
                </td></tr>
              )}
              {pageRows.map(c => (
                <tr
                  key={c.id}
                  style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer' }}
                  onClick={() => openEdit(c)}
                >
                  <td style={{ padding: '8px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontWeight: 500 }}>{c.companyName || '—'}</span>
                      {c.orgId && <IconLink size={11} style={{ color: '#16a34a', flexShrink: 0 }} title="Linked to Organizations Directory" />}
                    </div>
                  </td>
                  <td style={{ padding: '8px 16px' }}>{c.contractNumber || '—'}</td>
                  <td style={{ padding: '8px 16px' }}>
                    <Text size="sm">{c.contactName || '—'}</Text>
                    {c.contactEmail && <Text size="xs" c="dimmed">{c.contactEmail}</Text>}
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <Badge size="sm" color={STATUS_COLORS[c.status] ?? '#939598'}>{c.status}</Badge>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                    <Group gap={4} justify="flex-end">
                      {canEdit && (
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(c)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                      )}
                      {canEdit && (
                        <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={() => setDeleteTarget(c)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {totalPages > 1 && (
        <Group justify="center" mt="md" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────── */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contractor' : 'Add Contractor'}
        centered
        size="lg"
      >
        <Stack gap="md">
          {loadingOrg ? (
            <Text size="sm" c="dimmed">Loading organization details…</Text>
          ) : linkedOrg ? (
            /* ── Org linked: show card, hide identity inputs ── */
            <OrgLinkedCard org={linkedOrg} onClear={clearOrg} />
          ) : (
            /* ── No org linked: search + manual entry ── */
            <>
              {TABLES.organizations && <OrgSearch onSelect={selectOrg} />}

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                <Text size="xs" fw={600} c="dimmed" mb={8}>Company Details</Text>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  <TextInput
                    label="Company Name"
                    required
                    value={form.companyName ?? ''}
                    onChange={e => setField('companyName', e.target.value)}
                    style={{ gridColumn: 'span 2' }}
                  />
                  <TextInput
                    label="Contact Name"
                    value={form.contactName ?? ''}
                    onChange={e => setField('contactName', e.target.value)}
                  />
                  <TextInput
                    label="Contact Phone"
                    value={form.contactPhone ?? ''}
                    onChange={e => setField('contactPhone', e.target.value)}
                  />
                  <TextInput
                    label="Contact Email"
                    value={form.contactEmail ?? ''}
                    onChange={e => setField('contactEmail', e.target.value)}
                    style={{ gridColumn: 'span 2' }}
                  />
                </SimpleGrid>

                {TABLES.organizations && !editTarget && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 14, cursor: 'pointer', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={saveToDir}
                      onChange={e => setSaveToDir(e.target.checked)}
                    />
                    <span>Save to Organizations Directory for future incidents</span>
                    {saveToDir && <IconCheck size={13} style={{ color: '#16a34a' }} />}
                  </label>
                )}
              </div>
            </>
          )}

          {/* ── Incident-specific fields — always shown ── */}
          <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Text size="xs" fw={600} c="dimmed" mb={8}>
              {activeIncident?.name ? `${activeIncident.name} — Engagement` : 'Incident Engagement'}
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
              <TextInput
                label="Contract Number"
                value={form.contractNumber ?? ''}
                onChange={e => setField('contractNumber', e.target.value)}
              />
              <TextInput
                label="Contract Amount ($)"
                type="number"
                value={form.contractAmount ?? ''}
                onChange={e => setField('contractAmount', e.target.value)}
              />
              <Select
                label="Status"
                data={STATUS_OPTIONS}
                value={(form.status as ContractorStatus) ?? 'active'}
                onChange={v => setField('status', v ?? 'active')}
                style={{ gridColumn: 'span 2' }}
              />
            </SimpleGrid>
          </div>

          <Paper p="xs" radius="sm" withBorder style={{ background: '#F8F8F8' }}>
            <Text size="sm" c="dimmed">
              Rates are managed in the{' '}
              <Text span fw={600} c="var(--color-primary-700)" component="a" href="/debris/rates" style={{ cursor: 'pointer' }}>
                Rate Schedule
              </Text>
              {' '}page with FEMA-compliant line items and distance tiers.
            </Text>
          </Paper>

          <Textarea
            label="Notes"
            value={form.notes ?? ''}
            onChange={e => setField('notes', e.target.value)}
            autosize
            minRows={2}
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add Contractor'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete confirmation ───────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Contractor" centered size="sm">
        <Stack>
          <Text size="sm">
            Remove <strong>{deleteTarget?.companyName}</strong> from this incident?
            {deleteTarget?.orgId && (
              <Text span size="sm" c="dimmed"> Their organization record will remain in the directory.</Text>
            )}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Remove</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
